import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import { listScheduledJobs, listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import WorkflowList from "modules/workflow/components/WorkflowList";
import { useUser } from "context/UserContext";

export type WorkflowFilter = "all" | "live" | "paused";

interface WorkflowListContainerProps {
  analystsList: HeaderWithStats[];
  isLoadingAnalysts: boolean;
}

const WorkflowListContainer: React.FC<WorkflowListContainerProps> = ({ analystsList, isLoadingAnalysts }) => {
  const navigate = useNavigate();
  const { defaultAnalystId } = useUser();

  // Jobs state
  const [jobs, setJobs] = useState<Header[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // UI state
  const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState<WorkflowFilter>("all");

  const fetchJobs = useCallback(async (analystFilter: Header | null) => {
    setIsLoadingJobs(true);
    if (analystFilter?.id) {
      const response = await listScheduledJobsByOwnerId(analystFilter.id, true);
      const scenarioJobs = (response ?? []).filter(
        (job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB
      );
      setJobs(scenarioJobs.map((job: ScheduledJobProto) => job.header).filter((header): header is Header => !!header));
    } else {
      const response = await listScheduledJobs("SCENARIO_JOB", -1, 1, "", "LAST_MODIFIED", false, true, "");
      setJobs(response || []);
    }
    setIsLoadingJobs(false);
  }, []);

  // Load all analysts' scheduled jobs by default on mount
  useEffect(() => {
    const defaultAnalyst = analystsList.find(a => a.id === defaultAnalystId);
    setSelectedAnalyst(defaultAnalyst || null);
    if (defaultAnalyst) {
      fetchJobs(defaultAnalyst);
    }
  }, [fetchJobs, analystsList]);

  const liveCount = useMemo(() => jobs.filter(j => j.has_active_schedule === true).length, [jobs]);

  // Client-side live/paused and search filters applied on top of the fetched list
  const filteredJobs = useMemo(() => {
    let result = jobs;
    if (filterMode === "live") result = result.filter(j => j.has_active_schedule === true);
    else if (filterMode === "paused") result = result.filter(j => j.has_active_schedule === false);
    if (searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(j => j.name?.toLowerCase().includes(lower));
    }
    return result;
  }, [jobs, searchQuery, filterMode]);

  const handleAnalystSelect = useCallback(
    (analyst: Header | null) => {
      setSelectedAnalyst(analyst);
      fetchJobs(analyst);
    },
    [fetchJobs]
  );

  const handleJobClick = useCallback(
    (job: Header) => {
      if (job.id) navigate(`/workflows/${job.id}/definition`);
    },
    [navigate]
  );

  const handleDefinitionClick = useCallback(
    (job: Header) => {
      if (job.id) navigate(`/workflows/${job.id}/definition`);
    },
    [navigate]
  );

  const handlePastRunsClick = useCallback(
    (job: Header) => {
      if (job.id) navigate(`/workflows/${job.id}/runs`);
    },
    [navigate]
  );

  const handleCreateNew = useCallback(() => {
    if (selectedAnalyst?.id) {
      navigate("/chat/new", {
        state: {
          initialPrompt:
            "I want to create a new workflow. Guide me through the process and ask me the details" +
            " required to setup a workflow. Save when I am done.",
          analystId: selectedAnalyst.id,
        },
      });
    }
  }, [navigate, selectedAnalyst]);

  const handleRefresh = useCallback(() => {
    fetchJobs(selectedAnalyst);
  }, [fetchJobs, selectedAnalyst]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <WorkflowList
      filteredJobs={filteredJobs}
      isLoadingJobs={isLoadingJobs}
      isLoadingAnalysts={isLoadingAnalysts}
      analystsList={analystsList}
      selectedAnalyst={selectedAnalyst}
      searchQuery={searchQuery}
      filterMode={filterMode}
      totalCount={jobs.length}
      liveCount={liveCount}
      onSearchChange={handleSearchChange}
      onAnalystSelect={handleAnalystSelect}
      onFilterChange={setFilterMode}
      onJobClick={handleJobClick}
      onDefinitionClick={handleDefinitionClick}
      onPastRunsClick={handlePastRunsClick}
      onRefresh={handleRefresh}
      onCreateNew={handleCreateNew}
    />
  );
};

export default WorkflowListContainer;
